import React, { useCallback, useEffect } from "react";
import useAnimalMeasurementContext from "../../../hooks/useAnimalMeasurementContext";
import { useSelector } from "react-redux";
import moment from "moment";

import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Icon,
  Input,
  InputNumber,
  Menu,
  Row,
  Select,
  Table,
  notification,
} from "antd";

import { numberMask } from "../../../services/helpersMethodsService";
import { deleteAnimalMeasurement } from "../../../services/animalMeasurementService";
import CustomLink from "../../../components/utils/customLink";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import MenuIcon from "../../../components/utils/table/icons/menu";

const TableAnimalMeasurement = () => {
  const {
    handleTableChange,
    fetchData,
    initializeData,
    openOrCloseForm,
    dataList,
    isLoadingDataList,
    tableProperties: { size, page, search, tableFilters, tableSorters },
  } = useAnimalMeasurementContext();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const { Column } = Table;

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleEdit = (record) => {
    initializeData({
      id: record.id,
      handlingNumber: record.handlingNumber,
      animalId: record.animalId,
      measurementId: record.measurementId,
      value: record.value != null ? Number(record.value) : null,
      measurementDate: record.measurementDate,
      lotDestinationId: null,
      saveOptions: "only_save",
    });
    openOrCloseForm();
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteAnimalMeasurement({ groupId, farmId, id });
        fetchData(page, size, {}, search);
        notification.success({
          message: translation.animalMeasurement.form.messages.deleteSuccess,
        });
      } catch (error) {
        notification.error({
          message: translation.animalMeasurement.form.messages.deleteError,
        });
      }
    },
    [farmId, fetchData, groupId, page, search, size, translation]
  );

  const menu = (id, record) => (
    <Menu>
      {record.origination === "manual" && (
        <Menu.Item onClick={() => handleEdit(record)}>
          {translation.table.menu.edit}
        </Menu.Item>
      )}
      <Menu.Item onClick={() => handleDelete(id)}>
        {translation.table.menu.delete}
      </Menu.Item>
    </Menu>
  );

  const getColumnSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys && selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon="search"
            size="small"
            style={{
              width: 90,
              marginRight: 8,
              background: "#684e94",
              border: "none",
            }}
          />
          <Button
            onClick={() => handleReset(clearFilters)}
            icon="delete"
            size="small"
            style={{ width: 90 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
    }),
    [handleReset, handleSearch]
  );

  const getColumnDateSearchProps = useCallback(
    () => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Row type="flex" gutter={4}>
            <Col>
              <Select
                value={selectedKeys ? selectedKeys[1] : "equal"}
                onChange={(value) =>
                  setSelectedKeys(
                    value
                      ? selectedKeys
                        ? [selectedKeys[0], value]
                        : [null, value]
                      : []
                  )
                }
              >
                <Select.Option value="equal">=</Select.Option>
                <Select.Option value="greaterThan">&gt;</Select.Option>
                <Select.Option value="greaterOrEqualThan">&ge;</Select.Option>
                <Select.Option value="lessThan">&lt;</Select.Option>
                <Select.Option value="lessOrEqualThan">&le;</Select.Option>
              </Select>
            </Col>
            <Col>
              <DatePicker
                value={selectedKeys && selectedKeys[0]}
                format={"DD/MM/YYYY"}
                onChange={(date) =>
                  setSelectedKeys(
                    date
                      ? selectedKeys && selectedKeys.length > 1
                        ? [date, selectedKeys[1]]
                        : [date, "equal"]
                      : []
                  )
                }
                style={{ width: 188, marginBottom: 8, display: "block" }}
              />
            </Col>
          </Row>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon="search"
            size="small"
            style={{
              width: 90,
              marginRight: 8,
              background: "#684e94",
              border: "none",
            }}
          />
          <Button
            onClick={() => handleReset(clearFilters)}
            icon="delete"
            size="small"
            style={{ width: 90 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
    }),
    [handleReset, handleSearch]
  );

  const getColumnNumberSearchProps = useCallback(
    () => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Row type="flex" gutter={4}>
            <Col>
              <Select
                value={selectedKeys ? selectedKeys[1] : "equal"}
                onChange={(value) =>
                  setSelectedKeys(
                    value
                      ? selectedKeys
                        ? [selectedKeys[0], value]
                        : [null, value]
                      : []
                  )
                }
              >
                <Select.Option value="equal">=</Select.Option>
                <Select.Option value="greaterThan">&gt;</Select.Option>
                <Select.Option value="greaterOrEqualThan">&ge;</Select.Option>
                <Select.Option value="lessThan">&lt;</Select.Option>
                <Select.Option value="lessOrEqualThan">&le;</Select.Option>
              </Select>
            </Col>
            <Col>
              <InputNumber
                value={selectedKeys && selectedKeys[0]}
                min={0}
                precision={2}
                step={0.01}
                onChange={(value) =>
                  setSelectedKeys(
                    value
                      ? selectedKeys && selectedKeys.length > 1
                        ? [value, selectedKeys[1]]
                        : [value, "equal"]
                      : []
                  )
                }
                style={{ width: 188, marginBottom: 8, display: "block" }}
              />
            </Col>
          </Row>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon="search"
            size="small"
            style={{
              width: 90,
              marginRight: 8,
              background: "#684e94",
              border: "none",
            }}
          />
          <Button
            onClick={() => handleReset(clearFilters)}
            icon="delete"
            size="small"
            style={{ width: 90 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
    }),
    [handleReset, handleSearch]
  );

  useEffect(() => {
    handleTableChange();
  }, [handleTableChange]);

  return (
    <Table
      rowKey="id"
      loading={isLoadingDataList}
      dataSource={dataList != null ? dataList.content : []}
      pagination={{
        total: dataList != null ? dataList.totalElements : 0,
        size: dataList != null ? size : 0,
        current: dataList != null ? page : 0,
        hideOnSinglePage:
          dataList !== null && Object.entries(dataList).length !== 0
            ? dataList.totalElements > 10
              ? false
              : true
            : true,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
      }}
      scroll={{ x: 1200 }}
      onChange={handleTableChange}
    >
      <Column
        title={translation.animalMeasurement.table.columns.origination}
        dataIndex="origination"
        sorter
        key="origination"
        filteredValue={tableFilters?.origination || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "origination" && tableSorters.order
        }
        filtered
        filters={[
          { text: "BovManager", value: "bovManager" },
          { text: "Importação", value: "import" },
          { text: "Manual", value: "manual" },
        ]}
        filterMultiple={true}
        render={(text) => (
          <span>{translation.animalMeasurement.origination[text] || "-"}</span>
        )}
      />
      <Column
        title={translation.animalMeasurement.table.columns.handlingNumber}
        dataIndex="handlingNumber"
        sorter
        key="handlingNumber"
        filteredValue={tableFilters?.handlingNumber || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "handlingNumber" && tableSorters.order
        }
        {...getColumnSearchProps("handlingNumber")}
        render={(text, record) => (
          <CustomLink to={`/admin/animals/${record.animalId}`}>
            {text}
          </CustomLink>
        )}
      />
      <Column
        title={translation.animalMeasurement.table.columns.breedName}
        dataIndex="breedName"
        sorter
        key="breedName"
        filteredValue={tableFilters?.breedName || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "breedName" && tableSorters.order
        }
        {...getColumnSearchProps("breedName")}
      />
      <Column
        title={translation.animalMeasurement.table.columns.gender}
        dataIndex="gender"
        sorter
        key="gender"
        filteredValue={tableFilters?.gender || null}
        align="left"
        width={100}
        sortOrder={tableSorters?.columnKey === "gender" && tableSorters.order}
        filtered
        filters={[
          { text: translation.animalMeasurement.gender.M, value: "M" },
          { text: translation.animalMeasurement.gender.F, value: "F" },
        ]}
        filterMultiple={true}
        render={(text) => (
          <span>{translation.animalMeasurement.gender[text] || "-"}</span>
        )}
      />
      <Column
        title={translation.animalMeasurement.table.columns.measurementDate}
        dataIndex="measurementDate"
        sorter
        key="measurementDate"
        filteredValue={tableFilters?.measurementDate || null}
        align="left"
        width={130}
        sortOrder={
          tableSorters?.columnKey === "measurementDate" && tableSorters.order
        }
        {...getColumnDateSearchProps("measurementDate")}
        render={(text) => <span>{`${moment(text).format("DD/MM/YYYY")}`}</span>}
      />
      <Column
        title={translation.animalMeasurement.table.columns.originationLotName}
        dataIndex="originationLotName"
        sorter
        key="originationLotName"
        filteredValue={tableFilters?.originationLotName || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "originationLotName" && tableSorters.order
        }
        {...getColumnSearchProps("originationLotName")}
      />
      <Column
        title={
          translation.animalMeasurement.table.columns.originationPicketName
        }
        dataIndex="originationPicketName"
        sorter
        key="originationPicketName"
        filteredValue={tableFilters?.originationPicketName || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "originationPicketName" &&
          tableSorters.order
        }
        {...getColumnSearchProps("originationPicketName")}
      />
      <Column
        title={translation.animalMeasurement.table.columns.measurementName}
        dataIndex="measurementName"
        sorter
        key="measurementName"
        filteredValue={tableFilters?.measurementName || null}
        align="left"
        width={150}
        sortOrder={
          tableSorters?.columnKey === "measurementName" && tableSorters.order
        }
        {...getColumnSearchProps("measurementName")}
      />
      <Column
        title={translation.animalMeasurement.table.columns.value}
        dataIndex="value"
        sorter
        key="value"
        filteredValue={tableFilters?.value || null}
        align="left"
        width={120}
        sortOrder={tableSorters?.columnKey === "value" && tableSorters.order}
        {...getColumnNumberSearchProps("value")}
        render={(text) => (
          <span>{text != null ? numberMask(Number(text), false) : "-"}</span>
        )}
      />
      <Column
        align="left"
        width={50}
        render={(text, record) =>
          (record.origination === "manual" ||
            record.origination === "import") && (
            <Dropdown
              overlay={menu(record.id, record)}
              trigger={["click"]}
              key={record.id}
            >
              <MenuContainer>
                <MenuIcon />
              </MenuContainer>
            </Dropdown>
          )
        }
      />
    </Table>
  );
};

export default TableAnimalMeasurement;
